import { useRef, useState, useEffect, useCallback, FC } from 'react';
import { connect } from 'react-redux';
import { trackChatOpen } from './helpers/tracking';
import { SESSION_CHAT_STATE_KEY } from './constants';
import { localStorage } from 'dibs-browser-storage';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { BuyerChatRespVariantLazy } from 'dibs-buyer-chat/exports/BuyerChatRespVariantLazy';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// types
import { PdpRespDealerChatLoader_viewer as Viewer } from './__generated__/PdpRespDealerChatLoader_viewer.graphql';

type Props = {
    viewer: Viewer;
    isInteractivePdpChat: boolean;
};

export const PdpRespDealerChatLoaderComponent: FC<Props> = ({ viewer, isInteractivePdpChat }) => {
    const chatVisibilityRef = useRef(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isChatVisible, setIsChatVisible] = useState(false);

    useEffect(() => {
        if (isChatVisible) {
            const { isMinimized: isInitiallyMinimized = false } =
                (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized: boolean }) || {};
            trackChatOpen(isInitiallyMinimized);
        }
    }, [isChatVisible]);

    useEffect(() => {
        const { isMinimized: isInitiallyMinimized = false } =
            (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized: boolean }) || {};
        setIsMinimized(isInitiallyMinimized);
    }, []);

    const handleVisibilityChange = useCallback(({ isVisible, boundingClientRect, disconnect }) => {
        if (isVisible || boundingClientRect?.top < 0) {
            setIsChatVisible(true);
            disconnect();
        }
    }, []);

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {isChatVisible && isInteractivePdpChat && (
                <BuyerChatRespVariantLazy viewer={viewer} isInitiallyMinimized={isMinimized} />
            )}
        </>
    );
};

type ReduxStoreType = {
    relayVariables: {
        variables: {
            isInteractivePdpChat: boolean;
        };
    };
};

function mapStateToProps(state: ReduxStoreType): { isInteractivePdpChat: boolean } {
    const {
        relayVariables: { variables },
    } = state;

    return {
        isInteractivePdpChat: !!variables?.isInteractivePdpChat,
    };
}

export const PdpRespDealerChatLoader = createFragmentContainer(
    connect(mapStateToProps)(PdpRespDealerChatLoaderComponent),
    {
        viewer: graphql`
            fragment PdpRespDealerChatLoader_viewer on Viewer {
                ...BuyerChatRespVariantLazy_viewer @include(if: $isInteractivePdpChat)
            }
        `,
    }
);
